import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CloudOff, ArrowLeft, Pencil, Eye, FlaskConical, MapPin, Users, Package,
  MessageSquare, Building2, Save, X, Info, Plus, Trash2, LifeBuoy,
  ShieldCheck, HardDrive, BarChart3, Globe, Images, Clock, CreditCard, ExternalLink,
  Briefcase, Newspaper, Star, Receipt,
} from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { CockpitQuickView } from "@/components/common/CockpitQuickView";
import { PLAN_CLS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { useT52 } from "@/i18n/v52";
import { Empty52 } from "@/components/v52/Bits";
import { KdHead, KdMetrics, KdNav, KdSelect, LockDialog } from "@/components/v52/KundeParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// API-Werte bleiben unverändert; nur die Anzeige wird über v52kd.js übersetzt (DE/FR/IT/EN).
const PLAN_OPTIONS = ["FREE", "LIGHT", "WEBPRO", "MULTI"];
const ITEM_STATUS = ["draft", "review", "available", "reserved", "sold", "hidden"];
const INQ_STATUS = ["new", "contacted", "closed"];
const ROLES = ["owner", "admin", "editor", "employee"];
const SERVICE_STATUS = ["active", "inactive", "archived"];
const NEWS_TYPE = ["news", "action"];
const SUB_STATUS = ["active", "trialing", "cancelled"];
const LOC_FIELDS = [["name", "f_name"], ["street", "f_street"], ["postal_code", "f_zip"], ["city", "f_city"], ["canton", "f_canton"], ["phone", "f_phone"], ["email", "f_email"], ["website", "f_website"]];
const lbl = (tOr, prefix, v) => tOr(`${prefix}${v}`, v || "—");
const opt = (tOr, prefix, v) => (v ? tOr(`${prefix}${v}`, v) : null);
const modeLbl = (t52, v) => (v ? t52(v === "multi" ? "kd_multi" : "kd_single") : null);
const roText = (t52, k) => `${t52("c_ro")} · ${t52(k)}`;
const mb = (b) => ((b || 0) / 1048576).toFixed(2);
const errToast = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function useData(url) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.get(url).then((r) => setData(r.data)).catch(() => setData({ __error: true }));
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

function Field({ label, value }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="flex justify-between gap-4 border-b border-fundio-border py-2.5 text-sm">
      <span className="text-fundio-muted">{label}</span>
      {empty ? <span className="italic text-fundio-faint">—</span> : <span className="text-right font-medium text-fundio-ink">{String(value)}</span>}
    </div>
  );
}
function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="card-surface rounded-xl p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-fundio-muted"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="font-display text-2xl font-semibold text-fundio-ink">{value ?? "—"}</div>
    </div>
  );
}
function RONote({ children }) {
  return <div className="mb-4 flex items-center gap-2 rounded-lg border border-fundio-border bg-fundio-subtle px-4 py-2 text-xs text-fundio-muted"><Info className="h-4 w-4" /><span>{children}</span></div>;
}
function Loading() { return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-fundio-gold" /></div>; }
function Unavail() { const { t52 } = useT52(); return <div className="p-6 text-sm text-status-danger">{t52("c_unavail")}</div>; }
function SaveLabel({ saving, icon: Icon = Save, label }) { return saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Icon className="h-4 w-4" /> {label}</>; }
const GOLD_BTN = "border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight";
const WHITE_BTN = "border border-fundio-border bg-white text-sm font-semibold text-fundio-ink hover:bg-fundio-subtle";

export default function BrockiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t52 } = useT52();
  const isSuper = user?.role === "super_admin";
  const [state, setState] = useState(null);
  const [statsRes] = useData(`/organizations/${id}/stats`);
  const [tab, setTab] = useState("overview");
  const [qvOpen, setQvOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const contentRef = useRef(null);

  const load = useCallback(() => {
    setState(null);
    api.get(`/organizations/${id}`).then((r) => setState(r.data)).catch((e) =>
      setState(e?.response?.status === 404 ? { not_found: true } : { service_available: false }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const back = (
    <button type="button" data-testid="org-detail-back" onClick={() => navigate("/brockis")} className="f52-back">
      <ArrowLeft aria-hidden="true" /> {t52("kd_back")}
    </button>
  );
  if (state === null)
    return <div className="f52-page" data-testid="org-detail-loading" aria-busy="true">{back}<span className="f52-sr">{t52("loading")}</span><span className="f52-skel block" /><span className="f52-skel block" /></div>;
  if (state.not_found)
    return <div className="f52-page">{back}<section className="f52-panel"><Empty52 icon={Building2} tone="neutral" title={t52("kd_notFound")} text={`Tenant-ID: ${id}`} testid="org-detail-not-found" /></section></div>;
  if (!state.service_available)
    return (
      <div className="f52-page">{back}
        <section className="f52-panel">
          <Empty52 icon={CloudOff} tone="warn" role="alert" title={t52("k_downT")} text={state.reason || t52("kd_downD")} testid="org-detail-unavailable" />
        </section>
      </div>
    );

  const org = state.organization || {};
  const isQa = !!state.annotation?.is_demo;
  const stats = statsRes?.stats || {};
  const active = String(org.status || "").toLowerCase() === "active" || String(org.status || "").toLowerCase() === "aktiv";

  // Schreibaktion nur nach expliziter Bestätigung im Dialog (bestehender API-Vertrag: PUT /organizations/:id).
  const toggleAccount = async () => {
    const next = active ? "suspended" : "active";
    try { await api.put(`/organizations/${id}`, { status: next }); toast.success(t52(active ? "m_locked" : "m_unlocked")); setLockOpen(false); load(); }
    catch (e) { errToast(e); }
  };
  const goto = (k) => { setTab(k); requestAnimationFrame(() => contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })); };
  const name = org.company_name || org.name || t52("c_orgFallback");

  return (
    <div className="f52-page" data-testid="org-detail-page">
      {back}
      {isQa && (
        <div className="f52-qa-note" role="note" data-testid="org-detail-qa-banner"><strong>PREVIEW / INTERNAL QA</strong><span>{t52("kd_qaNote")}</span></div>
      )}
      <KdHead org={{ ...org, tenant_id: org.tenant_id || id }} stats={stats} name={name} active={active} isSuper={isSuper}
        onGoto={goto} onCockpit={() => setQvOpen(true)} onLock={() => setLockOpen(true)} />
      <KdMetrics stats={stats} />

      <div className="f52-kd-layout">
        <KdNav tab={tab} onTab={setTab} />
        <div className="f52-kd-main" ref={contentRef}>
          <KdSelect tab={tab} onTab={setTab} />
          <div className="min-w-0" data-testid="org-tab-panel">
      {tab === "overview" && <OverviewTab org={org} stats={stats} />}
      {tab === "firma" && <FirmaTab org={org} isSuper={isSuper} onSaved={load} />}
      {tab === "standorte" && <StandorteTab id={id} isSuper={isSuper} />}
      {tab === "hours" && <HoursTab id={id} isSuper={isSuper} />}
      {tab === "services" && <ServicesTab id={id} isSuper={isSuper} />}
      {tab === "news" && <NewsTab id={id} isSuper={isSuper} />}
      {tab === "items" && <ItemsTab id={id} isSuper={isSuper} />}
      {tab === "media" && <MediaTab id={id} isSuper={isSuper} />}
      {tab === "inquiries" && <InquiriesTab id={id} isSuper={isSuper} />}
      {tab === "reviews" && <ReviewsTab id={id} isSuper={isSuper} />}
      {tab === "stats" && <StatsTab stats={stats} loading={!statsRes} err={statsRes?.__error || statsRes?.service_available === false} />}
      {tab === "website" && <WebsiteTab org={org} />}
      {tab === "plan" && <PlanTab org={org} stats={stats} isSuper={isSuper} onSaved={load} />}
      {tab === "billing" && <BillingTab id={id} isSuper={isSuper} />}
      {tab === "team" && <TeamTab id={id} isSuper={isSuper} />}
      {tab === "support" && <SupportTab id={id} org={org} onOpenCockpit={() => setQvOpen(true)} />}
      {tab === "security" && <SecurityTab org={org} isQa={isQa} />}
      {tab === "storage" && <StorageTab id={id} />}
          </div>
        </div>
      </div>

      {lockOpen && <LockDialog name={name} active={active} onCancel={() => setLockOpen(false)} onConfirm={toggleAccount} />}
      {qvOpen && (
        <CockpitQuickView tenants={[{ tenant_id: id, company_name: org.company_name || org.name, plan: org.plan, account_mode: org.account_mode }]} initialTenantId={id} onClose={() => setQvOpen(false)} />
      )}
    </div>
  );
}

function OverviewTab({ org, stats }) {
  const { t52, tOr } = useT52();
  const c = org.contact || {};
  const rows = [
    ["f_company", org.company_name || org.name], ["tenant_id", org.tenant_id], ["f_plan", org.plan], ["f_status", lbl(tOr, "orgst_", org.status)],
    ["f_accountMode", modeLbl(t52, org.account_mode)], ["f_customerStatus", opt(tOr, "cs_", org.customer_status)], ["f_claimStatus", opt(tOr, "cl_", org.claim_status)], ["f_city", org.city || c.city],
    ["f_website", org.external_website], ["f_address", org.address || c.address], ["f_contact", c.name], ["f_storageCost", org.storage_cost_per_gb_chf],
    ["f_items", stats.items_total], ["f_lastActivity", org.last_activity ? formatDate(org.last_activity) : null],
  ];
  return (
    <Panel title={t52("kd_tab_overview")} testid="org-overview-panel">
      <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
        {rows.map(([k, v]) => <Field key={k} label={k === "tenant_id" ? k : t52(k)} value={v} />)}
      </div>
    </Panel>
  );
}

function FirmaTab({ org, isSuper, onSaved }) {
  const { t52 } = useT52();
  const c = org.contact || {};
  const [f, setF] = useState({
    company_name: org.company_name || "", external_website: org.external_website || "", address: org.address || "",
    account_mode: org.account_mode || "single", customer_status: org.customer_status || "", claim_status: org.claim_status || "",
    contact_name: c.name || "", contact_email: c.email || "", contact_phone: c.phoneDisplay || "", contact_mobile: c.mobileDisplay || "",
    contact_tagline: c.tagline || "", contact_city: c.city || "", contact_region: c.region || "",
  });
  const [saving, setSaving] = useState(false);
  if (!isSuper) return <RONote>{roText(t52, "ro_firma")}</RONote>;

  const save = async () => {
    setSaving(true);
    try {
      const p = {};
      if (f.company_name !== (org.company_name || "")) p.company_name = f.company_name;
      if (f.external_website !== (org.external_website || "")) p.external_website = f.external_website;
      if (f.address !== (org.address || "")) p.address = f.address;
      if (f.account_mode !== org.account_mode) p.account_mode = f.account_mode;
      if (f.customer_status !== (org.customer_status || "")) p.customer_status = f.customer_status;
      if (f.claim_status !== (org.claim_status || "")) p.claim_status = f.claim_status;
      const contact = { ...c, name: f.contact_name, email: f.contact_email, phoneDisplay: f.contact_phone, mobileDisplay: f.contact_mobile, tagline: f.contact_tagline, city: f.contact_city, region: f.contact_region };
      if (["name", "email", "phoneDisplay", "mobileDisplay", "tagline", "city", "region"].some((k) => (contact[k] || "") !== (c[k] || ""))) p.contact = contact;
      if (!Object.keys(p).length) { toast.info(t52("m_noChanges")); setSaving(false); return; }
      await api.put(`/organizations/${org.tenant_id}`, p);
      toast.success(t52("m_firmaSaved")); onSaved?.();
    } catch (e) { errToast(e); }
    finally { setSaving(false); }
  };
  const T = (k, label, span) => (
    <div className={`space-y-1.5 ${span ? "col-span-2" : ""}`}><Label className="text-xs text-fundio-muted">{t52(label)}</Label>
      <Input data-testid={`org-edit-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-dark" /></div>
  );
  return (
    <Panel title={t52("p_firmaEdit")} testid="org-firma-panel">
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-4 py-2 text-xs text-status-info"><Building2 className="h-4 w-4" /><span>{t52("i_firmaSave")}</span></div>
      <div className="grid grid-cols-2 gap-4">
        {T("company_name", "f_companyName", true)}
        <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">{t52("f_accountMode")}</Label>
          <Select value={f.account_mode} onValueChange={(v) => setF({ ...f, account_mode: v })}><SelectTrigger data-testid="org-edit-account_mode" className="input-dark"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="single">{t52("kd_single")}</SelectItem><SelectItem value="multi">{t52("kd_multi")}</SelectItem></SelectContent></Select></div>
        {T("external_website", "f_website")}
        {T("address", "f_address", true)}
        {T("customer_status", "f_customerStatus")}
        {T("claim_status", "f_claimStatus")}
        <div className="col-span-2 mt-1 border-t border-fundio-border pt-3 text-xs font-semibold uppercase tracking-wide text-fundio-faint">{t52("f_contact")}</div>
        {T("contact_name", "f_contactName")}{T("contact_email", "f_email")}
        {T("contact_phone", "f_phone")}{T("contact_mobile", "f_mobile")}
        {T("contact_city", "f_city")}{T("contact_region", "f_region")}
        {T("contact_tagline", "f_tagline", true)}
      </div>
      <div className="mt-5 flex justify-end"><Button data-testid="org-edit-save" disabled={saving || !f.company_name} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} label={t52("c_save")} /></Button></div>
    </Panel>
  );
}

function StandorteTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/locations`);
  const [creating, setCreating] = useState(false);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_standorte")} testid="org-locations-panel"><Unavail /></Panel>;
  const locs = res.locations || [];
  return (
    <Panel title={`${t52("kd_tab_standorte")} (${locs.length})`} testid="org-locations-panel"
      actions={isSuper && <Button data-testid="org-location-create-btn" onClick={() => setCreating(true)} className={WHITE_BTN}><Plus className="h-4 w-4" /> {t52("b_newLoc")}</Button>}>
      {creating && <LocationCreateForm id={id} onDone={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />}
      {!locs.length && !creating ? <EmptyState icon={MapPin} title={t52("e_noLocs")} testid="org-locations-empty" /> : (
        <div className="mt-3 space-y-3">{locs.map((l) => <LocationRow key={l.id} id={id} loc={l} canEdit={isSuper} onSaved={reload} />)}</div>
      )}
    </Panel>
  );
}

function LocationRow({ id, loc, canEdit, onSaved }) {
  const { t52 } = useT52();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(loc);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const p = {}; LOC_FIELDS.forEach(([k]) => { if ((f[k] || "") !== (loc[k] || "")) p[k] = f[k] || ""; });
      if (!Object.keys(p).length) { setEditing(false); setSaving(false); return; }
      await api.put(`/organizations/${id}/locations/${loc.id}`, p); toast.success(t52("m_locSaved")); setEditing(false); onSaved?.();
    } catch (e) { errToast(e); } finally { setSaving(false); }
  };
  if (!editing) return (
    <div data-testid={`org-location-${loc.id}`} className="rounded-xl border border-fundio-border bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-fundio-ink"><MapPin className="h-4 w-4 text-fundio-gold" />{loc.name}{loc.is_primary && <span className="rounded border border-fundio-border bg-fundio-subtle px-1.5 py-0.5 text-[10px] font-semibold text-fundio-muted">{t52("l_primary")}</span>}</div>
          <div className="mt-1 text-xs text-fundio-muted">{[loc.street, `${loc.postal_code || ""} ${loc.city || ""}`.trim(), loc.canton].filter(Boolean).join(", ") || "—"}</div>
          <div className="mt-1 text-xs text-fundio-faint">{[loc.phone, loc.email, loc.website].filter(Boolean).join(" · ") || "—"}</div>
        </div>
        {canEdit && <button data-testid={`org-location-edit-${loc.id}`} onClick={() => { setF(loc); setEditing(true); }} className="inline-flex items-center gap-1 rounded-lg border border-fundio-border px-2.5 py-1.5 text-xs font-medium text-fundio-ink hover:bg-fundio-subtle"><Pencil className="h-3.5 w-3.5" /> {t52("c_edit")}</button>}
      </div>
    </div>
  );
  return (
    <div data-testid={`org-location-edit-form-${loc.id}`} className="rounded-xl border border-fundio-gold/40 bg-white p-4">
      <div className="grid grid-cols-2 gap-3">
        {LOC_FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1 ${k === "name" ? "col-span-2" : ""}`}><Label className="text-xs text-fundio-muted">{t52(label)}</Label>
            <Input data-testid={`org-location-field-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-dark" /></div>
        ))}
      </div>
      <div className="mt-3 flex justify-end gap-2"><Button variant="ghost" onClick={() => setEditing(false)} className="text-fundio-muted"><X className="h-4 w-4" /> {t52("c_cancel")}</Button>
        <Button data-testid={`org-location-save-${loc.id}`} disabled={saving} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} label={t52("c_save")} /></Button></div>
    </div>
  );
}

function LocationCreateForm({ id, onDone, onCancel }) {
  const { t52 } = useT52();
  const [f, setF] = useState({ name: "", street: "", postal_code: "", city: "", canton: "", phone: "", email: "", website: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v)); await api.post(`/organizations/${id}/locations`, p); toast.success(t52("m_locCreated")); onDone?.(); }
    catch (e) { errToast(e); } finally { setSaving(false); }
  };
  return (
    <div data-testid="org-location-create-form" className="mb-3 rounded-xl border border-fundio-gold/40 bg-white p-4">
      <div className="mb-2 text-sm font-semibold text-fundio-ink">{t52("b_newLoc")}</div>
      <div className="grid grid-cols-2 gap-3">
        {LOC_FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1 ${k === "name" ? "col-span-2" : ""}`}><Label className="text-xs text-fundio-muted">{t52(label)}{k === "name" && " *"}</Label>
            <Input data-testid={`org-loc-new-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-dark" /></div>
        ))}
      </div>
      <div className="mt-3 flex justify-end gap-2"><Button variant="ghost" onClick={onCancel} className="text-fundio-muted"><X className="h-4 w-4" /> {t52("c_cancel")}</Button>
        <Button data-testid="org-loc-new-save" disabled={saving || !f.name} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} icon={Plus} label={t52("c_create")} /></Button></div>
    </div>
  );
}

function HoursTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/locations`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_hours")} testid="org-hours-panel"><Unavail /></Panel>;
  const locs = res.locations || [];
  return (
    <Panel title={t52("kd_tab_hours")} testid="org-hours-panel">
      {!isSuper && <RONote>{roText(t52, "ro_hours")}</RONote>}
      {!locs.length ? <EmptyState icon={Clock} title={t52("e_noLocs")} testid="org-hours-empty" /> : (
        <div className="space-y-4">{locs.map((l) => <HoursEditor key={l.id} id={id} loc={l} canEdit={isSuper} onSaved={reload} />)}</div>
      )}
    </Panel>
  );
}

function HoursEditor({ id, loc, canEdit, onSaved }) {
  const { t52 } = useT52();
  const initial = Array.isArray(loc.opening_hours) ? loc.opening_hours.map((r) => (Array.isArray(r) ? [r[0] || "", r[1] || ""] : ["", String(r)])) : [];
  const [rows, setRows] = useState(initial.length ? initial : [["Mo-Fr", ""]]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const cleaned = rows.filter((r) => r[0] || r[1]); await api.put(`/organizations/${id}/locations/${loc.id}`, { opening_hours: cleaned }); toast.success(t52("m_hoursSaved")); onSaved?.(); }
    catch (e) { errToast(e); } finally { setSaving(false); }
  };
  return (
    <div data-testid={`org-hours-${loc.id}`} className="rounded-xl border border-fundio-border bg-white p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-fundio-ink"><Clock className="h-4 w-4 text-fundio-gold" />{loc.name}</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input data-testid={`org-hours-label-${loc.id}-${i}`} disabled={!canEdit} value={r[0]} placeholder={t52("ph_days")} onChange={(e) => setRows(rows.map((x, j) => j === i ? [e.target.value, x[1]] : x))} className="input-dark w-40" />
            <Input data-testid={`org-hours-value-${loc.id}-${i}`} disabled={!canEdit} value={r[1]} placeholder="09:00 - 18:00" onChange={(e) => setRows(rows.map((x, j) => j === i ? [x[0], e.target.value] : x))} className="input-dark flex-1" />
            {canEdit && <button aria-label={t52("a_removeRow")} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-fundio-faint hover:text-status-danger"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="mt-3 flex justify-between">
          <button data-testid={`org-hours-add-${loc.id}`} onClick={() => setRows([...rows, ["", ""]])} className="inline-flex items-center gap-1 text-xs font-medium text-fundio-muted hover:text-fundio-ink"><Plus className="h-3.5 w-3.5" /> {t52("b_row")}</button>
          <Button data-testid={`org-hours-save-${loc.id}`} disabled={saving} onClick={save} className={`${GOLD_BTN} text-sm`}><SaveLabel saving={saving} label={t52("c_save")} /></Button>
        </div>
      )}
    </div>
  );
}

function useRowPut(url, onSaved) {
  const [busy, setBusy] = useState(false);
  const put = async (body, okMsg) => { setBusy(true); try { await api.put(url, body); toast.success(okMsg); onSaved?.(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  return [busy, put];
}

function OptSelect({ value, onChange, busy, values, prefix, testid, className, label }) {
  const { tOr } = useT52();
  return (
    <Select value={value} onValueChange={onChange} disabled={busy}>
      <SelectTrigger data-testid={testid} aria-label={label} className={`input-dark ${className}`}><SelectValue /></SelectTrigger>
      <SelectContent>{values.map((v) => <SelectItem key={v} value={v}>{lbl(tOr, prefix, v)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function CardField({ label, children }) { return <div className="f52-cfield"><span>{label}</span>{children}</div>; }

function ItemsTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/items`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_items")} testid="org-items-panel"><Unavail /></Panel>;
  const items = res.items || [];
  return (
    <Panel title={`${t52("kd_tab_items")} (${items.length})`} testid="org-items-panel" noPad>
      {!isSuper && <div className="p-3"><RONote>{roText(t52, "ro_items")}</RONote></div>}
      {!items.length ? <div className="p-4"><EmptyState icon={Package} title={t52("e_noItems")} testid="org-items-empty" /></div> : (
        <><div className="f52-tbl-wrap"><table className="w-full text-sm"><thead><tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
          <th className="px-5 py-3">{t52("f_title")}</th><th className="px-5 py-3">{t52("th_category")}</th><th className="px-5 py-3">{t52("th_price")}</th><th className="px-5 py-3">{t52("f_status")}</th><th className="px-5 py-3 text-right">{t52("th_action")}</th></tr></thead>
          <tbody className="divide-y divide-fundio-border">{items.map((it) => <ItemRow key={it.id} id={id} it={it} canEdit={isSuper} onSaved={reload} />)}</tbody>
        </table></div>
        <ul className="f52-cards kd" data-testid="org-items-cards">{items.map((it) => <ItemCard key={it.id} id={id} it={it} canEdit={isSuper} onSaved={reload} />)}</ul></>
      )}
    </Panel>
  );
}

function useItemStatus(id, it, onSaved) {
  const { t52, tOr } = useT52();
  const [busy, put] = useRowPut(`/organizations/${id}/items/${it.id}`, onSaved);
  return [busy, (status) => put({ status }, t52("m_status", { v: lbl(tOr, "st_item_", status) }))];
}

function ItemCard({ id, it, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, setStatus] = useItemStatus(id, it, onSaved);
  return (
    <li data-testid={`org-item-card-${it.id}`} className="f52-ccard">
      <p className="cn">{it.title}</p>
      <dl className="f52-cdl two">
        <div><dt>{t52("th_category")}</dt><dd className="txt">{it.category || "—"}</dd></div>
        <div><dt>{t52("th_price")}</dt><dd>{it.price != null ? `CHF ${it.price}` : "—"}</dd></div>
        {!canEdit && <div><dt>{t52("f_status")}</dt><dd className="txt">{lbl(tOr, "st_item_", it.status)}</dd></div>}
      </dl>
      {canEdit && (
        <>
          <CardField label={t52("f_status")}><OptSelect value={it.status} onChange={setStatus} busy={busy} values={ITEM_STATUS} prefix="st_item_" testid={`org-item-card-status-${it.id}`} label={t52("f_status")} className="h-12 w-full" /></CardField>
          <div className="f52-cact kd">
            <button type="button" data-testid={`org-item-card-publish-${it.id}`} disabled={busy || it.status === "available"} onClick={() => setStatus("available")} className="f52-btn f52-btn-ok">{t52("c_publish")}</button>
            <button type="button" data-testid={`org-item-card-archive-${it.id}`} disabled={busy || it.status === "hidden"} onClick={() => setStatus("hidden")} className="f52-btn f52-btn-secondary">{t52("c_archive")}</button>
          </div>
        </>
      )}
    </li>
  );
}

function ItemRow({ id, it, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, setStatus] = useItemStatus(id, it, onSaved);
  return (
    <tr data-testid={`org-item-${it.id}`} className="bg-white">
      <td className="px-5 py-3 font-medium text-fundio-ink">{it.title}</td>
      <td className="px-5 py-3 text-fundio-muted">{it.category || "—"}</td>
      <td className="px-5 py-3 text-fundio-muted">{it.price != null ? `CHF ${it.price}` : "—"}</td>
      <td className="px-5 py-3">
        {canEdit ? <OptSelect value={it.status} onChange={setStatus} busy={busy} values={ITEM_STATUS} prefix="st_item_" testid={`org-item-status-${it.id}`} className="h-8 w-40" />
          : <span className="text-fundio-muted">{lbl(tOr, "st_item_", it.status)}</span>}
      </td>
      <td className="px-5 py-3 text-right">
        {canEdit && (
          <div className="flex justify-end gap-2">
            <button data-testid={`org-item-publish-${it.id}`} disabled={busy || it.status === "available"} onClick={() => setStatus("available")} className="rounded-lg border border-status-success-border bg-status-success-bg px-2.5 py-1 text-xs font-semibold text-status-success disabled:opacity-40">{t52("c_publish")}</button>
            <button data-testid={`org-item-archive-${it.id}`} disabled={busy || it.status === "hidden"} onClick={() => setStatus("hidden")} className="rounded-lg border border-fundio-border bg-white px-2.5 py-1 text-xs font-semibold text-fundio-muted disabled:opacity-40">{t52("c_archive")}</button>
          </div>
        )}
      </td>
    </tr>
  );
}

function MediaTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/media`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_media")} testid="org-media-panel"><Unavail /></Panel>;
  const media = res.media || [];
  const usage = res.usage || {};
  const pct = usage.quota_bytes ? Math.min(100, Math.round((usage.total_bytes / usage.quota_bytes) * 100)) : 0;
  const del = async (mid) => {
    if (!window.confirm(t52("q_imgDelete"))) return;
    try { await api.delete(`/organizations/${id}/media/${mid}`); toast.success(t52("m_imgDeleted")); reload(); }
    catch (e) { errToast(e); }
  };
  const th = "px-2 py-2 font-medium";
  return (
    <Panel title={`${t52("kd_tab_media")} (${res.count ?? media.length})`} testid="org-media-panel">
      <div className="mb-4 rounded-xl border border-fundio-border bg-white p-4">
        <div className="mb-1.5 flex items-center justify-between text-sm"><span className="font-medium text-fundio-ink">{t52("l_quota")}</span><span className="text-fundio-muted">{mb(usage.total_bytes)} / {mb(usage.quota_bytes)} MB</span></div>
        <div className="h-2 overflow-hidden rounded-full bg-fundio-subtle"><div data-testid="org-media-quota-bar" className="h-full rounded-full bg-fundio-gold" style={{ width: `${pct}%` }} /></div>
      </div>
      {!media.length ? <EmptyState icon={Images} title={t52("e_noMedia")} description={t52("ed_noMedia")} testid="org-media-empty" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint"><th className={th}>{t52("th_path")}</th><th className={th}>{t52("th_size")}</th><th className={th}>{t52("th_dim")}</th><th className={th}>{t52("th_created")}</th><th className="px-2 py-2" /></tr></thead>
          <tbody className="divide-y divide-fundio-border">{media.map((m) => (
            <tr key={m.id} data-testid={`org-media-${m.id}`} className="hover:bg-fundio-subtle">
              <td className="max-w-md truncate px-2 py-3 font-mono text-xs text-fundio-muted" title={m.image_path}>{m.image_path}</td>
              <td className="px-2 py-3 text-fundio-ink">{Math.round((m.original_bytes || m.processed_bytes || 0) / 1024)} KB</td>
              <td className="px-2 py-3 text-fundio-muted">{m.width && m.height ? `${m.width}×${m.height}` : "—"}</td>
              <td className="px-2 py-3 text-fundio-faint">{m.created_at ? formatDate(m.created_at) : "—"}</td>
              <td className="px-2 py-3 text-right">{isSuper && <button data-testid={`org-media-delete-${m.id}`} onClick={() => del(m.id)} className="inline-flex items-center gap-1 rounded-lg border border-status-danger-border bg-status-danger-bg px-2.5 py-1 text-xs font-semibold text-status-danger"><Trash2 className="h-3.5 w-3.5" /> {t52("c_delete")}</button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Panel>
  );
}

function InquiriesTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/inquiries`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_inquiries")} testid="org-inquiries-panel"><Unavail /></Panel>;
  const rows = res.inquiries || [];
  return (
    <Panel title={`${t52("kd_tab_inquiries")} (${rows.length})`} testid="org-inquiries-panel" noPad>
      {!rows.length ? <div className="p-4"><EmptyState icon={MessageSquare} title={t52("e_noInq")} testid="org-inquiries-empty" /></div> : (
        <><div className="f52-tbl-wrap"><table className="w-full text-sm"><thead><tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
          <th className="px-5 py-3">{t52("th_object")}</th><th className="px-5 py-3">{t52("th_prospect")}</th><th className="px-5 py-3">{t52("th_message")}</th><th className="px-5 py-3">{t52("th_received")}</th><th className="px-5 py-3">{t52("f_status")}</th></tr></thead>
          <tbody className="divide-y divide-fundio-border">{rows.map((q) => <InqRow key={q.id} id={id} q={q} canEdit={isSuper} onSaved={reload} />)}</tbody>
        </table></div>
        <ul className="f52-cards kd" data-testid="org-inquiries-cards">{rows.map((q) => <InqCard key={q.id} id={id} q={q} canEdit={isSuper} onSaved={reload} />)}</ul></>
      )}
    </Panel>
  );
}

function useInqStatus(id, q, onSaved) {
  const { t52 } = useT52();
  const [busy, put] = useRowPut(`/organizations/${id}/inquiries/${q.id}`, onSaved);
  return [busy, (status) => put({ status }, t52("m_statusUpdated"))];
}

function InqCard({ id, q, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, set] = useInqStatus(id, q, onSaved);
  return (
    <li data-testid={`org-inquiry-card-${q.id}`} className="f52-ccard">
      <p className="cn">{q.item_title || "—"}</p>
      <dl className="f52-cdl one">
        <div><dt>{t52("th_prospect")}</dt><dd className="txt">{q.name || "—"}{q.phone ? ` · ${q.phone}` : ""}</dd></div>
        <div><dt>{t52("th_message")}</dt><dd className="txt">{q.message || "—"}</dd></div>
        <div><dt>{t52("th_received")}</dt><dd className="txt">{q.created_at ? formatDate(q.created_at) : "—"}</dd></div>
        {!canEdit && <div><dt>{t52("f_status")}</dt><dd className="txt">{lbl(tOr, "st_inq_", q.status)}</dd></div>}
      </dl>
      {canEdit && <CardField label={t52("f_status")}><OptSelect value={q.status} onChange={set} busy={busy} values={INQ_STATUS} prefix="st_inq_" testid={`org-inquiry-card-status-${q.id}`} label={t52("f_status")} className="h-12 w-full" /></CardField>}
    </li>
  );
}

function InqRow({ id, q, canEdit, onSaved }) {
  const { tOr } = useT52();
  const [busy, set] = useInqStatus(id, q, onSaved);
  return (
    <tr data-testid={`org-inquiry-${q.id}`} className="bg-white">
      <td className="px-5 py-3 font-medium text-fundio-ink">{q.item_title || "—"}</td>
      <td className="px-5 py-3 text-fundio-muted">{q.name || "—"}{q.phone ? ` · ${q.phone}` : ""}</td>
      <td className="max-w-xs truncate px-5 py-3 text-fundio-muted">{q.message || "—"}</td>
      <td className="px-5 py-3 text-fundio-faint">{q.created_at ? formatDate(q.created_at) : "—"}</td>
      <td className="px-5 py-3">{canEdit ? <OptSelect value={q.status} onChange={set} busy={busy} values={INQ_STATUS} prefix="st_inq_" testid={`org-inquiry-status-${q.id}`} className="h-8 w-36" />
        : <span className="text-fundio-muted">{lbl(tOr, "st_inq_", q.status)}</span>}</td>
    </tr>
  );
}

function StatBar({ label, value, total }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm"><span className="w-28 text-fundio-muted">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-fundio-subtle"><div className="h-full rounded-full bg-fundio-gold" style={{ width: `${pct}%` }} /></div>
      <span className="w-8 text-right font-medium text-fundio-ink">{value}</span></div>
  );
}
function StatsTab({ stats, loading, err }) {
  const { t52, tOr } = useT52();
  if (loading) return <Loading />;
  if (err) return <Panel title={t52("kd_tab_stats")} testid="org-stats-panel"><Unavail /></Panel>;
  const ibs = stats.items_by_status || {}; const qbs = stats.inquiries_by_status || {};
  return (
    <Panel title={t52("kd_tab_stats")} testid="org-stats-panel">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Metric icon={Package} label={t52("s_objects")} value={stats.items_total} />
        <Metric icon={MessageSquare} label={t52("s_inq")} value={stats.inquiries_total} />
        <Metric icon={MapPin} label={t52("s_locs")} value={stats.locations} />
        <Metric icon={Users} label={t52("s_members")} value={stats.members} />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div><div className="mb-3 text-sm font-semibold text-fundio-ink">{t52("s_itemsByStatus")}</div><div className="space-y-2">{ITEM_STATUS.map((v) => <StatBar key={v} label={lbl(tOr, "st_item_", v)} value={ibs[v] || 0} total={stats.items_total} />)}</div></div>
        <div><div className="mb-3 text-sm font-semibold text-fundio-ink">{t52("s_inqByStatus")}</div><div className="space-y-2">{INQ_STATUS.map((v) => <StatBar key={v} label={lbl(tOr, "st_inq_", v)} value={qbs[v] || 0} total={stats.inquiries_total} />)}</div></div>
      </div>
    </Panel>
  );
}

function WebsiteTab({ org }) {
  const { t52 } = useT52();
  const url = org.external_website;
  return (
    <Panel title={t52("kd_tab_website")} testid="org-website-panel">
      {!url ? <EmptyState icon={Globe} title={t52("e_noWeb")} description={t52("ed_noWeb")} testid="org-website-empty" /> : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><span className="min-w-0 break-all font-mono text-sm text-fundio-muted">{url}</span>
            <a data-testid="org-website-open" href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-fundio-border px-3 py-1.5 text-xs font-medium text-fundio-ink hover:bg-fundio-subtle"><ExternalLink className="h-3.5 w-3.5" /> {t52("c_newTab")}</a></div>
          <div className="overflow-hidden rounded-xl border border-fundio-border bg-white"><iframe data-testid="org-website-iframe" title={t52("kd_tab_website")} src={url.startsWith("http") ? url : `https://${url}`} className="h-[520px] w-full" sandbox="allow-scripts allow-same-origin" /></div>
          <p className="mt-2 text-xs text-fundio-faint">{t52("i_frame")}</p>
        </>
      )}
    </Panel>
  );
}

function PlanTab({ org, stats, isSuper, onSaved }) {
  const { t52 } = useT52();
  const [plan, setPlan] = useState(org.plan || "FREE");
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await api.put(`/organizations/${org.tenant_id}`, { plan }); toast.success(t52("m_planUpdated")); onSaved?.(); } catch (e) { errToast(e); } finally { setSaving(false); } };
  return (
    <Panel title={t52("kd_tab_plan")} testid="org-plan-panel">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <Label className="text-xs text-fundio-muted">{t52("l_currentPlan")}</Label>
          {isSuper ? (
            <div className="mt-2 flex items-center gap-2">
              <Select value={plan} onValueChange={setPlan}><SelectTrigger data-testid="org-plan-select" className="input-dark w-40"><SelectValue /></SelectTrigger><SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              <Button data-testid="org-plan-save" disabled={saving || plan === org.plan} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} label={t52("c_save")} /></Button>
            </div>
          ) : <div className="mt-2"><span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[org.plan] || PLAN_CLS.FREE}`}>{org.plan || "—"}</span></div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Metric icon={Package} label={t52("s_objects")} value={stats.items_total} />
          <Metric icon={MessageSquare} label={t52("s_inq")} value={stats.inquiries_total} />
          <Metric icon={MapPin} label={t52("s_locs")} value={stats.locations} />
          <Metric icon={Images} label={t52("s_media")} value={stats.media_count} />
        </div>
      </div>
    </Panel>
  );
}

function TeamTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/memberships`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_team")} testid="org-team-panel"><Unavail /></Panel>;
  const rows = res.memberships || [];
  return (
    <Panel title={`${t52("kd_tab_team")} (${rows.length})`} testid="org-team-panel" noPad>
      <div className="p-3"><RONote>{t52("i_team")}</RONote></div>
      {!rows.length ? <div className="p-4"><EmptyState icon={Users} title={t52("e_noUsers")} testid="org-team-empty" /></div> : (
        <><div className="f52-tbl-wrap"><table className="w-full text-sm"><thead><tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
          <th className="px-5 py-3">{t52("f_name")}</th><th className="px-5 py-3">{t52("f_email")}</th><th className="px-5 py-3">{t52("f_status")}</th><th className="px-5 py-3">{t52("th_role")}</th></tr></thead>
          <tbody className="divide-y divide-fundio-border">{rows.map((m) => <TeamRow key={m.user_id} id={id} m={m} canEdit={isSuper} onSaved={reload} />)}</tbody>
        </table></div>
        <ul className="f52-cards kd" data-testid="org-team-cards">{rows.map((m) => <TeamCard key={m.user_id} id={id} m={m} canEdit={isSuper} onSaved={reload} />)}</ul></>
      )}
    </Panel>
  );
}

function useMemberRole(id, m, onSaved) {
  const { t52 } = useT52();
  const [busy, put] = useRowPut(`/organizations/${id}/memberships/${m.user_id}`, onSaved);
  return [busy, (role) => put({ role }, t52("m_roleUpdated"))];
}

function TeamCard({ id, m, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, set] = useMemberRole(id, m, onSaved);
  return (
    <li data-testid={`org-member-card-${m.user_id}`} className="f52-ccard">
      <p className="cn">{m.name || "—"}</p>
      <dl className="f52-cdl one">
        <div><dt>{t52("f_email")}</dt><dd className="txt">{m.email || "—"}</dd></div>
        <div><dt>{t52("f_status")}</dt><dd className="txt text-status-success">{lbl(tOr, "orgst_", m.status)}</dd></div>
        {!canEdit && <div><dt>{t52("th_role")}</dt><dd className="txt">{lbl(tOr, "role_", m.role)}</dd></div>}
      </dl>
      {canEdit && <CardField label={t52("th_role")}><OptSelect value={m.role} onChange={set} busy={busy} values={ROLES} prefix="role_" testid={`org-member-card-role-${m.user_id}`} label={t52("th_role")} className="h-12 w-full" /></CardField>}
    </li>
  );
}

function TeamRow({ id, m, canEdit, onSaved }) {
  const { tOr } = useT52();
  const [busy, set] = useMemberRole(id, m, onSaved);
  return (
    <tr data-testid={`org-member-${m.user_id}`} className="bg-white">
      <td className="px-5 py-3 font-medium text-fundio-ink">{m.name || "—"}</td>
      <td className="px-5 py-3 text-fundio-muted">{m.email || "—"}</td>
      <td className="px-5 py-3"><span className="text-xs font-medium text-status-success">{lbl(tOr, "orgst_", m.status)}</span></td>
      <td className="px-5 py-3">{canEdit ? <OptSelect value={m.role} onChange={set} busy={busy} values={ROLES} prefix="role_" testid={`org-member-role-${m.user_id}`} className="h-8 w-40" />
        : <span className="text-fundio-muted">{lbl(tOr, "role_", m.role)}</span>}</td>
    </tr>
  );
}

function SupportTab({ id, org, onOpenCockpit }) {
  const { t52 } = useT52();
  const [res] = useData(`/cockpit/sessions`);
  const sessions = (res?.sessions || []).filter((s) => s.tenant_id === id);
  const th = "px-2 py-2 font-medium";
  return (
    <Panel title={t52("kd_tab_support")} testid="org-support-panel">
      <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-fundio-border bg-white p-4">
        <div><div className="text-sm font-semibold text-fundio-ink">{t52("sp_cockpitT")}</div><div className="text-xs text-fundio-muted">{t52("sp_cockpitD", { name: org.company_name || id })}</div></div>
        <Button data-testid="org-support-open-cockpit" onClick={onOpenCockpit} className={GOLD_BTN}><Eye className="h-4 w-4" /> {t52("b_openCockpit")}</Button>
      </div>
      <div className="mb-2 text-sm font-semibold text-fundio-ink">{t52("sp_history")}</div>
      {res === null ? <Loading /> : !sessions.length ? <EmptyState icon={LifeBuoy} title={t52("e_noSessions")} description={t52("ed_noSessions")} testid="org-support-empty" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint"><th className={th}>Super Admin</th><th className={th}>{t52("th_started")}</th><th className={th}>{t52("th_validUntil")}</th><th className={th}>{t52("f_status")}</th></tr></thead>
          <tbody className="divide-y divide-fundio-border">{sessions.map((s) => (
            <tr key={s.jti} className="bg-white"><td className="px-2 py-3 text-fundio-muted">{s.super_admin_email}</td><td className="px-2 py-3 text-fundio-faint">{formatDate(s.created_at)}</td><td className="px-2 py-3 text-fundio-faint">{formatDate(s.expires_at)}</td><td className="px-2 py-3">{s.active ? <span className="text-xs font-medium text-status-success">{t52("sess_active")}</span> : <span className="text-xs text-fundio-muted">{t52("sess_ended")}</span>}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </Panel>
  );
}

function SecurityTab({ org, isQa }) {
  const { t52, tOr } = useT52();
  const rows = [
    ["f_accountStatus", lbl(tOr, "orgst_", org.status)],
    ["f_claimStatus", opt(tOr, "cl_", org.claim_status)],
    ["f_customerStatus", opt(tOr, "cs_", org.customer_status)],
    ["f_accountMode", modeLbl(t52, org.account_mode)],
    ["f_env", t52(isQa ? "env_qa" : "env_prod")],
  ];
  return (
    <Panel title={t52("kd_tab_security")} testid="org-security-panel">
      <RONote>{roText(t52, "ro_security")}</RONote>
      <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
        {rows.map(([k, v]) => <Field key={k} label={t52(k)} value={v} />)}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-fundio-border bg-fundio-subtle px-4 py-2 text-xs text-fundio-muted"><ShieldCheck className="h-4 w-4" /><span>{t52("i_security")}</span></div>
    </Panel>
  );
}

function StorageTab({ id }) {
  const { t52 } = useT52();
  const [res] = useData(`/organizations/${id}/media`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_storage")} testid="org-storage-panel"><Unavail /></Panel>;
  const usage = res.usage || {};
  const pct = usage.quota_bytes ? Math.min(100, Math.round((usage.total_bytes / usage.quota_bytes) * 100)) : 0;
  return (
    <Panel title={t52("kd_tab_storage")} testid="org-storage-panel">
      <div className="mb-3 flex flex-wrap gap-2"><span className="inline-flex items-center rounded-full border border-fundio-border bg-fundio-subtle px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-fundio-muted">{t52("c_ro")}</span><span className="inline-flex items-center rounded-full border border-status-danger-border bg-status-danger-bg px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-status-danger">NO CUTOVER</span></div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Metric icon={Images} label={t52("sto_records")} value={res.count ?? 0} />
        <Metric icon={HardDrive} label={t52("sto_used")} value={mb(usage.total_bytes)} />
        <Metric icon={BarChart3} label={t52("sto_quota")} value={mb(usage.quota_bytes)} />
        <Metric icon={BarChart3} label={t52("sto_usage")} value={`${pct}%`} />
      </div>
      <div className="mt-4 rounded-xl border border-status-warning-border bg-status-warning-bg/40 px-5 py-4 text-xs text-status-warning">
        <span className="font-semibold">{t52("sto_gapL")}</span> {t52("sto_gap")}
      </div>
    </Panel>
  );
}

// ================= SERVICES =================
function ServicesTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/services`);
  const [creating, setCreating] = useState(false);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_services")} testid="org-services-panel"><Unavail /></Panel>;
  const rows = res.services || [];
  return (
    <Panel title={`${t52("kd_tab_services")} (${rows.length})`} testid="org-services-panel"
      actions={isSuper && <Button data-testid="org-service-create-btn" onClick={() => setCreating(true)} className={WHITE_BTN}><Plus className="h-4 w-4" /> {t52("b_newSvc")}</Button>}>
      {!isSuper && <RONote>{roText(t52, "ro_services")}</RONote>}
      {creating && <ServiceForm id={id} onDone={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />}
      {!rows.length && !creating ? <EmptyState icon={Briefcase} title={t52("e_noSvc")} description={t52("ed_noSvc")} testid="org-services-empty" /> : (
        <div className="mt-3 space-y-2">{rows.map((s) => <ServiceRow key={s.id} id={id} s={s} canEdit={isSuper} onSaved={reload} />)}</div>
      )}
    </Panel>
  );
}
function ServiceForm({ id, onDone, onCancel }) {
  const { t52 } = useT52();
  const [f, setF] = useState({ name: "", description: "", service_type: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await api.post(`/organizations/${id}/services`, Object.fromEntries(Object.entries(f).filter(([, v]) => v))); toast.success(t52("m_svcCreated")); onDone?.(); } catch (e) { errToast(e); } finally { setSaving(false); } };
  return (
    <div data-testid="org-service-create-form" className="mb-3 rounded-xl border border-fundio-gold/40 bg-white p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_name")} *</Label><Input data-testid="org-service-new-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-dark" /></div>
        <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_type")}</Label><Input data-testid="org-service-new-type" value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className="input-dark" /></div>
        <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_desc")}</Label><Input data-testid="org-service-new-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="input-dark" /></div>
      </div>
      <div className="mt-3 flex justify-end gap-2"><Button variant="ghost" onClick={onCancel} className="text-fundio-muted"><X className="h-4 w-4" /> {t52("c_cancel")}</Button>
        <Button data-testid="org-service-new-save" disabled={saving || !f.name} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} icon={Plus} label={t52("c_create")} /></Button></div>
    </div>
  );
}
function ServiceRow({ id, s, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(s);
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => { setBusy(true); try { await api.put(`/organizations/${id}/services/${s.id}`, { status }); toast.success(t52("m_status", { v: lbl(tOr, "st_svc_", status) })); onSaved?.(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  const save = async () => { setBusy(true); try { const p = {}; ["name", "description", "service_type"].forEach((k) => { if ((f[k] || "") !== (s[k] || "")) p[k] = f[k] || ""; }); if (Object.keys(p).length) await api.put(`/organizations/${id}/services/${s.id}`, p); toast.success(t52("m_svcSaved")); setEditing(false); onSaved?.(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  const del = async () => { if (!window.confirm(t52("q_svcDelete"))) return; try { await api.delete(`/organizations/${id}/services/${s.id}`); toast.success(t52("m_svcDeleted")); onSaved?.(); } catch (e) { errToast(e); } };
  return (
    <div data-testid={`org-service-${s.id}`} className="rounded-xl border border-fundio-border bg-white p-4">
      {!editing ? (
        <div className="flex items-start justify-between gap-3">
          <div><div className="flex items-center gap-2 text-sm font-semibold text-fundio-ink"><Briefcase className="h-4 w-4 text-fundio-gold" />{s.name}</div>
            <div className="mt-1 text-xs text-fundio-muted">{[s.service_type, s.description].filter(Boolean).join(" · ") || "—"}</div></div>
          <div className="flex items-center gap-2">
            {canEdit ? (
              <Select value={s.status} onValueChange={setStatus} disabled={busy}><SelectTrigger data-testid={`org-service-status-${s.id}`} className="input-dark h-8 w-32"><SelectValue /></SelectTrigger><SelectContent>{SERVICE_STATUS.map((v) => <SelectItem key={v} value={v}>{lbl(tOr, "st_svc_", v)}</SelectItem>)}</SelectContent></Select>
            ) : <span className="text-xs text-fundio-muted">{lbl(tOr, "st_svc_", s.status)}</span>}
            {canEdit && <><button data-testid={`org-service-edit-${s.id}`} aria-label={t52("c_edit")} onClick={() => { setF(s); setEditing(true); }} className="rounded-lg border border-fundio-border px-2 py-1.5 text-xs text-fundio-ink hover:bg-fundio-subtle"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`org-service-delete-${s.id}`} aria-label={t52("c_delete")} onClick={del} className="rounded-lg border border-status-danger-border bg-status-danger-bg px-2 py-1.5 text-xs text-status-danger"><Trash2 className="h-3.5 w-3.5" /></button></>}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_name")}</Label><Input data-testid={`org-service-name-${s.id}`} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-dark" /></div>
          <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_type")}</Label><Input value={f.service_type || ""} onChange={(e) => setF({ ...f, service_type: e.target.value })} className="input-dark" /></div>
          <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_desc")}</Label><Input value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} className="input-dark" /></div>
          <div className="col-span-2 flex justify-end gap-2"><Button variant="ghost" aria-label={t52("c_cancel")} onClick={() => setEditing(false)} className="text-fundio-muted"><X className="h-4 w-4" /></Button><Button data-testid={`org-service-save-${s.id}`} disabled={busy} onClick={save} className={GOLD_BTN}><Save className="h-4 w-4" /> {t52("c_save")}</Button></div>
        </div>
      )}
    </div>
  );
}

// ================= AKTIONEN & NEWS =================
function NewsTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/news`);
  const [creating, setCreating] = useState(false);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_news")} testid="org-news-panel"><Unavail /></Panel>;
  const rows = res.news || [];
  return (
    <Panel title={`${t52("kd_tab_news")} (${rows.length})`} testid="org-news-panel"
      actions={isSuper && <Button data-testid="org-news-create-btn" onClick={() => setCreating(true)} className={WHITE_BTN}><Plus className="h-4 w-4" /> {t52("b_newPost")}</Button>}>
      {!isSuper && <RONote>{roText(t52, "ro_news")}</RONote>}
      {creating && <NewsForm id={id} onDone={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />}
      {!rows.length && !creating ? <EmptyState icon={Newspaper} title={t52("e_noPosts")} description={t52("ed_noPosts")} testid="org-news-empty" /> : (
        <div className="mt-3 space-y-2">{rows.map((n) => <NewsRow key={n.id} id={id} n={n} canEdit={isSuper} onSaved={reload} />)}</div>
      )}
    </Panel>
  );
}
function NewsForm({ id, onDone, onCancel }) {
  const { t52, tOr } = useT52();
  const [f, setF] = useState({ title: "", body: "", type: "news" });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await api.post(`/organizations/${id}/news`, { title: f.title, body: f.body, type: f.type }); toast.success(t52("m_newsCreated")); onDone?.(); } catch (e) { errToast(e); } finally { setSaving(false); } };
  return (
    <div data-testid="org-news-create-form" className="mb-3 rounded-xl border border-fundio-gold/40 bg-white p-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_title")} *</Label><Input data-testid="org-news-new-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input-dark" /></div>
        <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_type")}</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="org-news-new-type" className="input-dark"><SelectValue /></SelectTrigger><SelectContent>{NEWS_TYPE.map((v) => <SelectItem key={v} value={v}>{lbl(tOr, "nt_", v)}</SelectItem>)}</SelectContent></Select></div>
        <div className="col-span-3 space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_text")}</Label><Input data-testid="org-news-new-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="input-dark" /></div>
      </div>
      <div className="mt-3 flex justify-end gap-2"><Button variant="ghost" onClick={onCancel} className="text-fundio-muted"><X className="h-4 w-4" /> {t52("c_cancel")}</Button>
        <Button data-testid="org-news-new-save" disabled={saving || !f.title} onClick={save} className={GOLD_BTN}><SaveLabel saving={saving} icon={Plus} label={t52("c_create")} /></Button></div>
    </div>
  );
}
function NewsRow({ id, n, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, setBusy] = useState(false);
  const act = async (payload, msg) => { setBusy(true); try { await api.put(`/organizations/${id}/news/${n.id}`, payload); toast.success(msg); onSaved?.(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  const del = async () => { if (!window.confirm(t52("q_newsDelete"))) return; try { await api.delete(`/organizations/${id}/news/${n.id}`); toast.success(t52("m_newsDeleted")); onSaved?.(); } catch (e) { errToast(e); } };
  const chip = "rounded border border-fundio-border bg-fundio-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fundio-muted";
  return (
    <div data-testid={`org-news-${n.id}`} className="rounded-xl border border-fundio-border bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-fundio-ink"><Newspaper className="h-4 w-4 text-fundio-gold" />{n.title}
            <span className={chip}>{lbl(tOr, "nt_", n.type)}</span>
            <span className={chip}>{lbl(tOr, "st_news_", n.status)}</span>
          </div>
          <div className="mt-1 max-w-2xl truncate text-xs text-fundio-muted">{n.body || "—"}</div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid={`org-news-publish-${n.id}`} disabled={busy || n.status === "published"} onClick={() => act({ status: "published", published_at: new Date().toISOString() }, t52("m_published"))} className="rounded-lg border border-status-success-border bg-status-success-bg px-2.5 py-1 text-xs font-semibold text-status-success disabled:opacity-40">{t52("c_publish")}</button>
            <button data-testid={`org-news-archive-${n.id}`} disabled={busy || n.status === "archived"} onClick={() => act({ status: "archived" }, t52("m_archived"))} className="rounded-lg border border-fundio-border bg-white px-2.5 py-1 text-xs font-semibold text-fundio-muted disabled:opacity-40">{t52("c_archive")}</button>
            <button data-testid={`org-news-delete-${n.id}`} aria-label={t52("c_delete")} onClick={del} className="rounded-lg border border-status-danger-border bg-status-danger-bg px-2 py-1.5 text-xs text-status-danger"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

// ================= BEWERTUNGEN =================
function ReviewsTab({ id, isSuper }) {
  const { t52 } = useT52();
  const [res, reload] = useData(`/organizations/${id}/reviews`);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_reviews")} testid="org-reviews-panel"><Unavail /></Panel>;
  const rows = res.reviews || [];
  return (
    <Panel title={`${t52("kd_tab_reviews")} (${rows.length})`} testid="org-reviews-panel">
      {!isSuper && <RONote>{roText(t52, "ro_reviews")}</RONote>}
      {!rows.length ? <EmptyState icon={Star} title={t52("e_noReviews")} description={t52("ed_noReviews")} testid="org-reviews-empty" /> : (
        <div className="space-y-3">{rows.map((r) => <ReviewRow key={r.id} id={id} r={r} canEdit={isSuper} onSaved={reload} />)}</div>
      )}
    </Panel>
  );
}
function ReviewRow({ id, r, canEdit, onSaved }) {
  const { t52, tOr } = useT52();
  const [busy, setBusy] = useState(false);
  const author = r.author_name || r.name || r.author || t52("rv_anon");
  const text = r.comment || r.text || r.body || "";
  const rating = r.rating ?? r.stars;
  const mod = async (status, reason) => { setBusy(true); try { await api.put(`/organizations/${id}/reviews/${r.id}`, reason ? { status, moderation_reason: reason } : { status }); toast.success(t52("m_status", { v: lbl(tOr, "st_rev_", status) })); onSaved?.(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  const del = async () => { if (!window.confirm(t52("q_reviewDelete"))) return; try { await api.delete(`/organizations/${id}/reviews/${r.id}`); toast.success(t52("m_deleted")); onSaved?.(); } catch (e) { errToast(e); } };
  return (
    <div data-testid={`org-review-${r.id}`} className="rounded-xl border border-fundio-border bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-fundio-ink">{author}{rating != null && <span className="inline-flex items-center gap-0.5 text-status-warning">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < rating ? "fill-current" : "text-fundio-faint"}`} />)}</span>}
            <span className="rounded border border-fundio-border bg-fundio-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fundio-muted">{lbl(tOr, "st_rev_", r.status)}</span></div>
          <div className="mt-1 max-w-2xl text-xs text-fundio-muted">{text || "—"}</div>
          {(r.reply || r.response) && <div className="mt-1 text-xs text-fundio-faint">{t52("rv_reply", { v: r.reply || r.response })}</div>}
          {r.moderation_reason && <div className="mt-1 text-xs text-status-danger">{t52("rv_reason", { v: r.moderation_reason })}</div>}
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid={`org-review-publish-${r.id}`} disabled={busy} onClick={() => mod("published")} className="rounded-lg border border-status-success-border bg-status-success-bg px-2.5 py-1 text-xs font-semibold text-status-success">{t52("b_visible")}</button>
            <button data-testid={`org-review-hide-${r.id}`} disabled={busy} onClick={() => mod("hidden")} className="rounded-lg border border-fundio-border bg-white px-2.5 py-1 text-xs font-semibold text-fundio-muted">{t52("b_hide")}</button>
            <button data-testid={`org-review-reject-${r.id}`} disabled={busy} onClick={() => { const rs = window.prompt(t52("q_rejectReason")); if (rs !== null) mod("rejected", rs); }} className="rounded-lg border border-status-danger-border bg-status-danger-bg px-2.5 py-1 text-xs font-semibold text-status-danger">{t52("b_reject")}</button>
            <button data-testid={`org-review-delete-${r.id}`} aria-label={t52("c_delete")} onClick={del} className="rounded-lg border border-fundio-border bg-white px-2 py-1.5 text-xs text-fundio-muted"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

// ================= ABOS & RECHNUNGEN =================
function BillingTab({ id, isSuper }) {
  const { t52, tOr, lang } = useT52();
  const [res, reload] = useData(`/organizations/${id}/billing`);
  const [busy, setBusy] = useState(false);
  if (!res) return <Loading />;
  if (res.__error || res.service_available === false) return <Panel title={t52("kd_tab_billing")} testid="org-billing-panel"><Unavail /></Panel>;
  const b = res.billing || {}; const sub = b.subscription || {}; const ent = b.entitlements || {}; const invs = b.invoices || []; const pay = b.payment || {}; const li = b.preview_line_items || [];
  const act = async (fn, label) => { setBusy(true); try { await fn(); toast.success(label); reload(); } catch (e) { errToast(e); } finally { setBusy(false); } };
  const yn = (v) => t52(v ? "c_yes" : "c_no");
  const th = "px-2 py-2 font-medium";
  return (
    <Panel title={t52("kd_tab_billing")} testid="org-billing-panel">
      {!pay.provider && (
        <div data-testid="org-billing-nopay" className="mb-4 flex items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/50 px-4 py-2 text-xs text-status-warning">
          <CreditCard className="h-4 w-4" /><span><span className="font-semibold">{t52("bi_noPayT")}</span> {lang === "de" ? (pay.note || t52("bi_noPayD")) : t52("bi_noPayD")} {t52("bi_noAuto")}</span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-fundio-border bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-fundio-ink">{t52("bi_sub")}</div>
          <Field label={t52("f_plan")} value={b.plan} /><Field label={t52("f_status")} value={sub.status ? lbl(tOr, "st_sub_", sub.status) : null} /><Field label={t52("f_billing")} value={sub.billing_period ? lbl(tOr, "bp_", sub.billing_period) : null} />
          <Field label={t52("f_trialUntil")} value={sub.trial_end ? formatDate(sub.trial_end) : null} /><Field label={t52("f_start")} value={sub.started_at ? formatDate(sub.started_at) : null} />
          {isSuper && <SubEditForm id={id} sub={sub} plan={b.plan} onSaved={reload} />}
        </div>
        <div className="rounded-xl border border-fundio-border bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-fundio-ink">{t52("bi_ent")}</div>
          <Field label={t52("f_priceMonth")} value={ent.price_monthly_chf != null ? `CHF ${ent.price_monthly_chf}` : null} />
          <Field label={t52("f_usersIncl")} value={ent.users_included} /><Field label={t52("f_locsIncl")} value={ent.locations_included} />
          <Field label={t52("f_mediaQuota")} value={ent.media_quota_bytes ? `${mb(ent.media_quota_bytes)} MB` : null} />
          <Field label={t52("f_cockpit")} value={yn(ent.cockpit)} /><Field label={t52("f_domain")} value={yn(ent.custom_domain)} />
        </div>
      </div>
      <TrialCard id={id} trial={b.trial} isSuper={isSuper} onSaved={reload} />
      <div className="mb-2 mt-6 flex items-center justify-between">
        <div className="text-sm font-semibold text-fundio-ink">{t52("bi_invoices", { n: invs.length })}</div>
        {isSuper && <Button data-testid="org-invoice-generate" disabled={busy} onClick={() => act(() => api.post(`/organizations/${id}/billing/invoices`), t52("m_invCreated"))} className={WHITE_BTN}><Receipt className="h-4 w-4" /> {t52("b_invCreate")}</Button>}
      </div>
      {!invs.length ? <EmptyState icon={Receipt} title={t52("e_noInv")} description={t52("ed_noInv")} testid="org-invoices-empty" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint"><th className={th}>{t52("th_number")}</th><th className={th}>{t52("th_period")}</th><th className={th}>{t52("th_subtotal")}</th><th className={th}>{t52("th_vat")}</th><th className={th}>{t52("th_total")}</th><th className={th}>{t52("f_status")}</th><th className={th}>{t52("th_due")}</th><th className="px-2 py-2" /></tr></thead>
          <tbody className="divide-y divide-fundio-border">{invs.map((iv) => (
            <tr key={iv.id} data-testid={`org-invoice-${iv.id}`} className="hover:bg-fundio-subtle">
              <td className="px-2 py-3 font-mono text-xs text-fundio-ink">{iv.number}</td>
              <td className="px-2 py-3 text-fundio-muted">{iv.period ? `${iv.period.start} – ${iv.period.end}` : "—"}</td>
              <td className="px-2 py-3 text-fundio-muted">{iv.currency} {iv.subtotal}</td>
              <td className="px-2 py-3 text-fundio-muted">{iv.currency} {iv.tax}</td>
              <td className="px-2 py-3 font-medium text-fundio-ink">{iv.currency} {iv.total}</td>
              <td className="px-2 py-3"><span className={`text-xs font-medium ${iv.status === "paid" ? "text-status-success" : iv.status === "void" ? "text-fundio-muted" : "text-status-warning"}`}>{lbl(tOr, "st_inv_", iv.status)}</span></td>
              <td className="px-2 py-3 text-fundio-faint">{iv.due_at ? formatDate(iv.due_at) : "—"}</td>
              <td className="px-2 py-3 text-right">{isSuper && iv.status === "open" && (
                <div className="flex justify-end gap-2">
                  <button data-testid={`org-invoice-paid-${iv.id}`} disabled={busy} onClick={() => act(() => api.post(`/organizations/${id}/billing/invoices/${iv.id}/mark-paid`), t52("m_markedPaid"))} className="rounded-lg border border-status-success-border bg-status-success-bg px-2.5 py-1 text-xs font-semibold text-status-success">{t52("b_markPaid")}</button>
                  <button data-testid={`org-invoice-void-${iv.id}`} disabled={busy} onClick={() => act(() => api.post(`/organizations/${id}/billing/invoices/${iv.id}/void`), t52("m_voided"))} className="rounded-lg border border-fundio-border bg-white px-2.5 py-1 text-xs font-semibold text-fundio-muted">{t52("b_void")}</button>
                </div>)}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      {li.length > 0 && <div className="mt-4 text-xs text-fundio-faint">{t52("bi_preview")} {li.map((x, i) => <span key={i}>{x.description} (CHF {x.amount_chf}){i < li.length - 1 ? " · " : ""}</span>)}</div>}
    </Panel>
  );
}
function TrialCard({ id, trial, isSuper, onSaved }) {
  const { t52, tOr } = useT52();
  const t = trial || {};
  const [days, setDays] = useState("14");
  const [saving, setSaving] = useState(false);
  const started = !!t.started_at;
  const apply = async () => {
    setSaving(true);
    try {
      await api.put(`/organizations/${id}/trial`, { trial_days: Number(days) });
      toast.success(t52(started ? "m_trialExtended" : "m_trialSet"));
      onSaved?.();
    } catch (e) { errToast(e); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="org-trial-card" className="mt-4 rounded-xl border border-fundio-border bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-fundio-ink">
        <FlaskConical className="h-4 w-4 text-fundio-gold" /> {t52("tr_title")}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        <Field label={t52("f_package")} value={t.trial_days != null ? t52("c_days", { n: t.trial_days }) : null} />
        <Field label={t52("f_status")} value={t.status ? tOr(`st_trial_${t.status}`, t.status_label || t.status) : t.status_label} />
        <Field label={t52("f_daysLeft")} value={t.days_left != null ? t52("c_days", { n: t.days_left }) : null} />
        <Field label={t52("f_begin")} value={t.started_at ? formatDate(t.started_at) : t52("tr_beginPending")} />
        <Field label={t52("f_end")} value={t.ends_at ? formatDate(t.ends_at) : null} />
      </div>
      {isSuper && (
        <div className="mt-3 border-t border-fundio-border pt-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fundio-faint">
            {t52(started ? "tr_extendT" : "tr_controlT")}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-fundio-muted">{t52("f_duration")}</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid="org-trial-days" className="input-dark h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{[7, 14, 30].map((d) => <SelectItem key={d} value={String(d)}>{t52("c_days", { n: d })}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="org-trial-apply" disabled={saving} onClick={apply} className={`h-8 ${GOLD_BTN} text-sm`}>
              <SaveLabel saving={saving} label={t52(started ? "b_extend" : "b_set")} />
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-fundio-faint">{t52("tr_note")}</p>
        </div>
      )}
    </div>
  );
}
function SubEditForm({ id, sub, plan, onSaved }) {
  const { t52, tOr } = useT52();
  const [f, setF] = useState({ plan: plan || "FREE", status: sub.status || "active" });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await api.put(`/organizations/${id}/billing/subscription`, f); toast.success(t52("m_subUpdated")); onSaved?.(); } catch (e) { errToast(e); } finally { setSaving(false); } };
  return (
    <div className="mt-3 border-t border-fundio-border pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fundio-faint">{t52("se_title")}</div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_plan")}</Label><Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}><SelectTrigger data-testid="org-sub-plan" className="input-dark h-8 w-28"><SelectValue /></SelectTrigger><SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs text-fundio-muted">{t52("f_status")}</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="org-sub-status" className="input-dark h-8 w-32"><SelectValue /></SelectTrigger><SelectContent>{SUB_STATUS.map((v) => <SelectItem key={v} value={v}>{lbl(tOr, "st_sub_", v)}</SelectItem>)}</SelectContent></Select></div>
        <Button data-testid="org-sub-save" disabled={saving} onClick={save} className={`h-8 ${GOLD_BTN} text-sm`}><SaveLabel saving={saving} label={t52("c_save")} /></Button>
      </div>
      <p className="mt-2 text-[11px] text-fundio-faint">{t52("se_note")}</p>
    </div>
  );
}
